import { useState, useEffect, useMemo } from 'react';
import { Modal, Box, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import usePopup from 'hooks/usePopup';
import { useAuth } from 'hooks/useAuth';
import useApiWrapper from '../services/api/apiWrapper';
import { usePopupContext } from 'hooks/PopupContext';

const PopupModal = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { userInfo, setAndStoreUserInfo } = useAuth();
  const api = useApiWrapper();
  const {
    setPopupAndCardState,
    popupAndCardState,
    isApiCallInProgressRef
  } = usePopupContext()

  const popupData = useMemo(() => {
    return popupAndCardState
      .popupData
      .filter(item => {
        if ((!item.isClosed && (!item.isCardForm && !item.isCardByDefault))) {
          // OR should be removed once method-sept2025 is removed
          if (item?.sourceName || item.contentId.includes('method-sept2025')) {
            return popupAndCardState.qualifiedEvents.some(event => event.id === item.contentId)
          }
          return true
        }

        return false
      }) || []
  }, [popupAndCardState])

  const {
    renderPopupContent,
    handleDismissPopup: onClose,
  } = usePopup()

  useEffect(() => {
    // Reset index when modal opens/closes or data changes
    setCurrentIndex(0);
  }, [popupData]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % popupData.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + popupData.length) % popupData.length);
  };

  /**
   * Can handle both permanent closure or dismissal of the popup
   * @param {*} attributes
   * @param {string} mode - 'skip' or 'close'
   * @returns void
   */
  const handlePopupAttributeChange = async (attributes, mode = 'skip') => {
    const { popupId } = attributes
    isApiCallInProgressRef.current = true
    // Fake the permanent closure of the card before the api request
    // If the last item is closed, set the current index to 0
    setPopupAndCardState(prev => {
      const events = prev.popupData
      const index = events.findIndex(item => item.contentId === popupId)
      if (mode === 'skip') {
        events[index].isCardForm = true
      } else {
        events[index].isClosed = true
      }

      return { ...prev, popupData: events }
    })

    try {
      if (api && api.put) {
        await api.put(`${process.env.REACT_APP_OFFICE_BE_PH_URL}/v1/user/${userInfo?.unicityId}/popup-attributes`,
          attributes,
          {
            addPrefix: false,
            headers: {
              'Accept': 'application/json, text/plain, */*',
              'Content-Type': 'application/json',
            },
            withCredentials: true,
          }
        );
      }
    } catch (error) {
      console.error('[PopupModal] Error updating popup attributes:', error)
      // Don't throw - allow local state update to proceed
    }

    const popupAttributes = userInfo?.customAttributes?.popup || {}
    const updatedValues = {
      ...popupAttributes,
      [popupId]: {
        isCardForm: attributes.isCardForm,
        isClosed: attributes.isClosed,
      }
    }

    if (setAndStoreUserInfo) {
      setAndStoreUserInfo({
        ...userInfo,
        customAttributes: {
          ...userInfo?.customAttributes,
          popup: updatedValues,
        }
      })
    }
    isApiCallInProgressRef.current = false
  }

  return (
    popupData && popupData.length > 0 && (
      <Modal
        open={true}
        onClose={onClose}
        aria-labelledby="popup-modal"
        aria-describedby="popup-modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          data-test-id="popup-modal-content"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 40px)',
            minWidth: '220px',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: "12px",
            outline: 'none',
          }}
        >
          {popupData &&
            popupData.length > 0 &&
            renderPopupContent(popupData[currentIndex], handlePopupAttributeChange)
          }

          {popupData && popupData.length > 1 && (
            <Box data-test-id="carousel-buttons" sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'absolute',
              width: '100%',
              top: '50%',
              left: 0,
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              px: 1
            }}>
              <IconButton
                onClick={handlePrev}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.7)',
                  pointerEvents: 'auto'
                }}
                size="small"
                data-test-id="carousel-prev-button"
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.7)',
                  pointerEvents: 'auto'
                }}
                size="small"
                data-test-id="carousel-next-button"
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {popupData && popupData.length > 1 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              gap: 1
            }}>
              {popupData.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: currentIndex === index ? 'primary.main' : 'grey.400',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  data-test-id={`carousel-indicator-${index}`}
                />
              ))}
            </Box>
          )}
        </Box>
      </Modal>
    )
  );
};

export default PopupModal;
